// Web notification display — re-exports shared constants, provides web-specific routing
export {
  NOTIFICATION_TYPE_ICONS,
  NOTIFICATION_TYPE_LABELS,
  NOTIFICATION_FILTER_GROUPS,
  timeAgo,
} from '@task-tracker/shared';

/** Get the navigation route for a notification based on its type and data. */
export function getNotificationRoute(
  type: string,
  data: Record<string, unknown> | null,
): string | null {
  if (!data || typeof data !== 'object') return null;

  const getDataId = (...keys: string[]): string | undefined => {
    for (const key of keys) {
      const value = data[key];
      if (typeof value === 'string' && value.length > 0) {
        return value;
      }
    }

    return undefined;
  };

  const projectId = getDataId('projectId', 'project_id');
  const taskId = getDataId('taskId', 'task_id');
  const roleId = getDataId('newRoleId', 'new_role_id', 'roleId', 'role_id');

  switch (type) {
    // Task types → task detail page
    case 'task_assigned':
    case 'task_created':
    case 'task_updated':
    case 'task_status_changed':
    case 'task_ready_to_start':
    case 'task_completed':
    case 'task_reopened':
    case 'task_overdue':
    case 'task_at_risk':
    case 'task_reassigned':
    case 'deadline_reminder':
      return projectId && taskId
        ? `/projects/${projectId}/tasks/${taskId}`
        : projectId
          ? `/projects/${projectId}?tab=tasks`
          : null;

    case 'task_deleted':
      return projectId ? `/projects/${projectId}?tab=tasks` : null;

    // Document types → combined project documents and forms tab
    case 'document_shared':
    case 'document_uploaded':
    case 'document_version_uploaded':
    case 'document_updated':
      return projectId ? `/projects/${projectId}?tab=documents-forms` : null;

    // Note/comment/mention types → task detail (notes are shown in task view)
    case 'note_created':
    case 'comment_reply':
    case 'mention':
      return projectId && taskId ? `/projects/${projectId}/tasks/${taskId}` : null;

    // Project types → project detail
    case 'project_member_added':
    case 'project_member_removed':
    case 'project_created':
    case 'project_updated':
    case 'project_archived':
    case 'project_deleted':
      return projectId ? `/projects/${projectId}` : null;

    // Role types → role detail
    case 'role_changed':
      return roleId ? `/roles/${roleId}` : null;

    // Escalation → task if available
    case 'escalation_alert':
      return projectId && taskId
        ? `/projects/${projectId}/tasks/${taskId}`
        : projectId
          ? `/projects/${projectId}`
          : null;

    // User lifecycle → team page
    case 'user_invited':
    case 'user_deactivated':
    case 'user_reactivated':
      return '/team';

    default:
      return null;
  }
}
